import React, { useState } from 'react';

const MAX_WORDS = 23;

// Subject translations
const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Science', 
  'English', 'Arabic', 'French', 'Spanish', 'History', 'Geography', 
  'Computer Science', 'Programming', 'Art', 'Music', 'Physical Education', 'Other'
];

const SUBJECTS_AR = [
  'الرياضيات', 'الفيزياء', 'الكيمياء', 'الأحياء', 'العلوم',
  'الإنجليزية', 'العربية', 'الفرنسية', 'الإسبانية', 'التاريخ', 'الجغرافيا',
  'علوم الحاسوب', 'البرمجة', 'الفن', 'الموسيقى', 'التربية البدنية', 'أخرى'
];

// Language translations
const LANGUAGES = ['Arabic', 'English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Turkish'];
const LANGUAGES_AR = ['العربية', 'الإنجليزية', 'الفرنسية', 'الإسبانية', 'الألمانية', 'الإيطالية', 'البرتغالية', 'الروسية', 'الصينية', 'اليابانية', 'الكورية', 'الهندية', 'التركية'];

// Helper function to translate subject to Arabic
const translateSubject = (subject, isArabic) => {
  if (!subject || !isArabic) return subject;
  const index = SUBJECTS.indexOf(subject);
  return index !== -1 ? SUBJECTS_AR[index] : subject;
};

// Helper function to translate language to Arabic
const translateLanguage = (language, isArabic) => {
  if (!language || !isArabic) return language;
  const index = LANGUAGES.indexOf(language);
  return index !== -1 ? LANGUAGES_AR[index] : language;
};

// Helper function to transliterate common Arabic names from English to Arabic
const transliterateName = (name) => {
  if (!name) return name;
  
  // Common Arabic name mappings (English -> Arabic)
  const nameMappings = {
    'abdulla': 'عبدالله',
    'abdullah': 'عبدالله',
    'abdalla': 'عبدالله',
    'abdallah': 'عبدالله',
    'hilal': 'هلال',
    'hassan': 'حسن',
    'hussain': 'حسين',
    'hussein': 'حسين',
    'ahmed': 'أحمد',
    'ahmad': 'أحمد',
    'mohammed': 'محمد',
    'mohammad': 'محمد',
    'muhammad': 'محمد',
    'ali': 'علي',
    'omar': 'عمر',
    'umar': 'عمر',
    'khalid': 'خالد',
    'saleh': 'صالح',
    'nasser': 'ناصر',
    'youssef': 'يوسف',
    'yusuf': 'يوسف',
    'ibrahim': 'إبراهيم',
    'ismail': 'إسماعيل',
    'ismael': 'إسماعيل',
    'fatima': 'فاطمة',
    'mariam': 'مريم',
    'maryam': 'مريم',
    'noor': 'نور',
    'nur': 'نور',
    'layla': 'ليلى',
    'leila': 'ليلى',
    'sara': 'سارة',
    'sarah': 'سارة',
    'amina': 'أمينة',
    'amena': 'أمينة',
    'khadija': 'خديجة',
    'khadeeja': 'خديجة',
    'aisha': 'عائشة',
    'aishah': 'عائشة',
    'zainab': 'زينب',
    'zeinab': 'زينب',
    'salma': 'سلمى',
    'salama': 'سلمى'
  };
  
  // Split name into words and transliterate each
  const words = name.toLowerCase().split(/\s+/);
  const transliterated = words.map(word => {
    // Check if exact match exists
    if (nameMappings[word]) {
      return nameMappings[word];
    }
    // Check for partial matches (e.g., "abdulla" matches "abdullah")
    for (const [eng, arb] of Object.entries(nameMappings)) {
      if (word.includes(eng) || eng.includes(word)) {
        return arb;
      }
    }
    // If no match found, return original word
    return word;
  });
  
  return transliterated.join(' ');
};

export default function Step4Description({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [bio, setBio] = useState(wizardData.short_bio || '');
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState({});

  const wordCount = bio.trim() === '' ? 0 : bio.trim().split(/\s+/).filter(Boolean).length;

  const handleBioChange = (e) => {
    const text = e.target.value;
    const words = text.trim() === '' ? [] : text.trim().split(/\s+/).filter(Boolean);
    
    if (words.length <= MAX_WORDS) {
      setBio(text);
      setErrors({});
    } else {
      setErrors({ bio: `Bio must be ${MAX_WORDS} words or less. Currently ${words.length} words.` });
    }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    setErrors({});

    try {
      // Generate bio from wizard data
      const { full_name, subject, languages, experiences } = wizardData;
      
      // Build AI prompt from available data
      const promptParts = [];
      if (full_name) promptParts.push(`Name: ${full_name}`);
      if (subject) promptParts.push(`Teaches: ${subject}`);
      if (languages && languages.length > 0) {
        const langList = languages.map(l => `${l.language} (${l.level})`).join(', ');
        promptParts.push(`Languages: ${langList}`);
      }
      if (experiences && !experiences.experience_none && experiences.experiences && experiences.experiences.length > 0) {
        const expList = experiences.experiences.map(e => e.job_title || '').filter(Boolean).join(', ');
        if (expList) promptParts.push(`Experience: ${expList}`);
      }

      const prompt = `Generate a short professional bio (maximum ${MAX_WORDS} words) for a teacher on a tutoring platform. ${promptParts.join('. ')}. Make it friendly and professional.`;

      // Simulate AI delay for better UX
      await new Promise(resolve => setTimeout(resolve, 800));

      // Multiple bio templates for variation - Arabic and English versions
      let bioTemplates;
      
      if (isArabic) {
        // Arabic bio templates - translate everything to Arabic
        const subjectAr = translateSubject(subject, true) || 'المواد';
        const langAr = translateLanguage(languages?.[0]?.language, true) || 'عدة لغات';
        const nameAr = transliterateName(full_name) || 'أنا';
        
        bioTemplates = [
          `${nameAr} أدرس ${subjectAr} بـ ${langAr}. لدي خبرة وأنا متحمس لمساعدة الطلاب على النجاح.`,
          `${nameAr} معلم ${subjectAr} متفان. أتحدث ${langAr} وأحب مساعدة الطلاب على التعلم.`,
          `معلم ${subjectAr} متحمس مع خبرة في ${langAr}. ${nameAr} ملتزم بنجاح الطلاب.`,
          `${nameAr} أدرس ${subjectAr} وأتحدث ${langAr}. ملتزم بجعل التعلم ممتعاً وفعالاً.`,
          `معلم ${subjectAr} ذو خبرة. ${nameAr} أدرس بـ ${langAr} وأساعد الطلاب على تحقيق أهدافهم.`
        ];
      } else {
        // English bio templates
        bioTemplates = [
          `${full_name || 'I'} teach ${subject || 'subjects'} in ${languages?.[0]?.language || 'multiple languages'}. Experienced and passionate about helping students succeed.`,
          `${full_name || 'I'} am a dedicated ${subject || 'subject'} teacher. I speak ${languages?.[0]?.language || 'multiple languages'} and love helping students learn.`,
          `Passionate ${subject || 'subject'} teacher with experience in ${languages?.[0]?.language || 'multiple languages'}. ${full_name || 'I'} am committed to student success.`,
          `${full_name || 'I'} teach ${subject || 'subjects'} and speak ${languages?.[0]?.language || 'multiple languages'}. Dedicated to making learning fun and effective.`,
          `Experienced ${subject || 'subject'} teacher. ${full_name || 'I'} teach in ${languages?.[0]?.language || 'multiple languages'} and help students achieve their goals.`
        ];
      }

      // Randomly select a template for variation
      const randomTemplate = bioTemplates[Math.floor(Math.random() * bioTemplates.length)];
      const fallbackWords = randomTemplate.split(/\s+/).filter(Boolean);
      
      if (fallbackWords.length <= MAX_WORDS) {
        setBio(randomTemplate);
      } else {
        setBio(fallbackWords.slice(0, MAX_WORDS).join(' '));
      }
    } catch (error) {
      console.error('AI generation error:', error);
      // Fallback: simple template
      const { full_name, subject, languages } = wizardData;
      let fallbackBio;
      
      if (isArabic) {
        const subjectAr = translateSubject(subject, true) || 'المواد';
        const langAr = translateLanguage(languages?.[0]?.language, true) || 'عدة لغات';
        const nameAr = transliterateName(full_name) || 'أنا';
        fallbackBio = `${nameAr} أدرس ${subjectAr}${langAr ? ` بـ ${langAr}` : ''}. لدي خبرة وأنا متحمس لمساعدة الطلاب على النجاح.`;
      } else {
        fallbackBio = `${full_name || 'I'} teach ${subject || 'subjects'}${languages?.[0]?.language ? ` in ${languages[0].language}` : ''}. Experienced and passionate about helping students succeed.`;
      }
      
      const fallbackWords = fallbackBio.split(/\s+/).filter(Boolean);
      setBio(fallbackWords.slice(0, MAX_WORDS).join(' '));
      setErrors({ ai: isArabic ? 'استخدام القالب. يمكنك التعديل.' : 'Using template. You can edit this.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bio || bio.trim() === '') {
      setErrors({ bio: t.pleaseWriteBio || (isArabic ? 'يرجى كتابة نبذة قصيرة' : 'Please write a short bio') });
      return;
    }

    if (wordCount > MAX_WORDS) {
      setErrors({ bio: isArabic 
        ? `يجب أن تكون النبذة ${MAX_WORDS} كلمة أو أقل. حالياً ${wordCount} كلمة.`
        : `Bio must be ${MAX_WORDS} words or less. Currently ${wordCount} words.` });
      return;
    }

    await onSave({ short_bio: bio.trim() });
    onNext({ short_bio: bio.trim() });
  };

  return (
    <div>
      <h2 className="step-title">{t.description || 'Description'}</h2>
      <p className="step-subtitle">
        {t.descriptionSubtitle || 'Write a short bio about yourself. This helps students get to know you.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label>{t.shortBio || 'Short Bio'} *</label>
            <button
              type="button"
              onClick={generateWithAI}
              disabled={generating}
              style={{
                padding: '6px 12px',
                border: '1px solid #ff7a1a',
                borderRadius: '6px',
                background: 'white',
                color: '#ff7a1a',
                cursor: generating ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                opacity: generating ? 0.5 : 1
              }}
            >
              {generating ? (t.generating || 'Generating...') : (t.generateWithAI || 'Generate with AI')}
            </button>
          </div>
          <textarea
            value={bio}
            onChange={handleBioChange}
            placeholder="Write a short bio about yourself (max 23 words)..."
            rows={4}
            style={{ minHeight: '100px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: wordCount > MAX_WORDS ? '#dc2626' : '#6b7280' }}>
              {wordCount} / {MAX_WORDS} words
            </span>
          </div>
          {errors.bio && <span className="error-message">{errors.bio}</span>}
          {errors.ai && <span style={{ fontSize: '12px', color: '#6b7280' }}>{errors.ai}</span>}
        </div>

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({ short_bio: bio })}>
            Back
          </button>
          <button type="submit" className="btn btn-primary" disabled={!bio || wordCount > MAX_WORDS}>
            Save and continue
          </button>
        </div>
      </form>
    </div>
  );
}